import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const SECTIONS = [
  {
    id: "livekit",
    label: "LiveKit",
    fields: [
      { key: "livekit_url", label: "WebSocket URL", placeholder: "wss://your-project.livekit.cloud", secret: false },
      { key: "livekit_api_key", label: "API key", secret: true },
      { key: "livekit_api_secret", label: "API secret", secret: true },
    ],
  },
  {
    id: "stt",
    label: "STT",
    fields: [
      { key: "deepgram_api_key", label: "Deepgram API key", secret: true },
      { key: "assemblyai_api_key", label: "AssemblyAI API key", secret: true },
    ],
  },
  {
    id: "llm",
    label: "LLM",
    fields: [
      { key: "groq_api_key", label: "Groq API key", secret: true },
      { key: "openai_api_key", label: "OpenAI API key", secret: true },
    ],
  },
  {
    id: "tts",
    label: "TTS",
    fields: [
      { key: "cartesia_api_key", label: "Cartesia API key", secret: true },
      { key: "elevenlabs_api_key", label: "ElevenLabs API key", secret: true },
    ],
  },
];

export default function Settings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTab, setSavingTab] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get("/settings");
      setData(res.data);
    } catch (err) {
      toastError(err, "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const onSave = async (tabId, payload) => {
    setSavingTab(tabId);
    try {
      const res = await api.put("/settings", payload);
      setData(res.data);
      toast.success("Settings saved");
    } catch (err) {
      toastError(err, "Failed to save settings");
    } finally {
      setSavingTab(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" data-testid="settings-loading">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Provider credentials are encrypted at rest with a Fernet key. Empty
            a field and click <b>Save</b> to clear it.
          </p>
        </div>
        {data.updated_at && (
          <div
            className="text-xs text-muted-foreground"
            data-testid="settings-updated-at"
          >
            Last saved {new Date(data.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      <Tabs defaultValue="livekit" className="space-y-6">
        <TabsList data-testid="settings-tabs">
          {SECTIONS.map((s) => (
            <TabsTrigger key={s.id} value={s.id} data-testid={`settings-tab-${s.id}`}>
              {s.label}
            </TabsTrigger>
          ))}
          <TabsTrigger value="recordings" data-testid="settings-tab-recordings">
            Recordings
          </TabsTrigger>
          <TabsTrigger value="storage" data-testid="settings-tab-storage">
            Storage
          </TabsTrigger>
          <TabsTrigger value="worker" data-testid="settings-tab-worker">
            Worker
          </TabsTrigger>
        </TabsList>

        {SECTIONS.map((section) => (
          <TabsContent key={section.id} value={section.id}>
            <SecretSection
              section={section}
              data={data}
              saving={savingTab === section.id}
              onSave={(payload) => onSave(section.id, payload)}
            />
          </TabsContent>
        ))}

        <TabsContent value="recordings">
          <RecordingsSection
            data={data}
            saving={savingTab === "recordings"}
            onSave={(payload) => onSave("recordings", payload)}
          />
        </TabsContent>
        <TabsContent value="storage">
          <StorageSection
            data={data}
            saving={savingTab === "storage"}
            onSave={(payload) => onSave("storage", payload)}
          />
        </TabsContent>
        <TabsContent value="worker">
          <WorkerSection
            data={data}
            saving={savingTab === "worker"}
            onSave={(payload) => onSave("worker", payload)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SecretSection({ section, data, saving, onSave }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(section.fields.map((f) => [f.key, ""]))
  );

  const submit = (e) => {
    e.preventDefault();
    // Build PUT body: only include keys the user typed into.
    const payload = {};
    for (const f of section.fields) {
      const v = values[f.key];
      if (v === "") continue; // unchanged
      if (v === "__CLEAR__") {
        payload[f.key] = ""; // sentinel: clear
      } else {
        payload[f.key] = v;
      }
    }
    if (Object.keys(payload).length === 0) {
      toast.message("No changes to save");
      return;
    }
    onSave(payload);
    setValues(Object.fromEntries(section.fields.map((f) => [f.key, ""])));
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-5 rounded-lg border border-border bg-card p-6"
      data-testid={`settings-section-${section.id}`}
    >
      {section.fields.map((f) => {
        const has = data[`has_${f.key}`];
        const masked = has ? "***" : "";
        return (
          <div key={f.key} className="grid grid-cols-3 items-center gap-4">
            <Label htmlFor={f.key} className="col-span-1">
              {f.label}
            </Label>
            <div className="col-span-2 flex gap-2">
              {f.secret ? (
                <Input
                  id={f.key}
                  type="password"
                  value={values[f.key] || ""}
                  placeholder={has ? "*** (set — type to overwrite)" : "(not set)"}
                  data-testid={`settings-${f.key}`}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, [f.key]: e.target.value }))
                  }
                />
              ) : (
                <Input
                  id={f.key}
                  value={
                    values[f.key] !== "" ? values[f.key] : data[f.key] || ""
                  }
                  placeholder={f.placeholder}
                  data-testid={`settings-${f.key}`}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, [f.key]: e.target.value }))
                  }
                />
              )}
              {has && f.secret && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setValues((s) => ({ ...s, [f.key]: "__CLEAR__" }))
                  }
                  data-testid={`settings-${f.key}-clear`}
                  title="Clear this secret"
                >
                  <X className="h-4 w-4" /> Clear
                </Button>
              )}
            </div>
            {values[f.key] === "__CLEAR__" && (
              <p className="col-span-3 text-xs text-destructive">
                Marked for clearing — click Save to confirm.
              </p>
            )}
            {f.secret && masked && values[f.key] === "" && (
              <p className="col-span-3 text-xs text-muted-foreground">
                Currently set: {masked}
              </p>
            )}
          </div>
        );
      })}
      <div className="flex justify-end">
        <Button type="submit" disabled={saving} data-testid={`settings-${section.id}-save`}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save {section.label}
        </Button>
      </div>
    </form>
  );
}

function RecordingsSection({ data, saving, onSave }) {
  const [days, setDays] = useState(data.default_recording_retention_days);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ default_recording_retention_days: Number(days) });
      }}
      className="space-y-5 rounded-lg border border-border bg-card p-6"
      data-testid="settings-section-recordings"
    >
      <div className="grid grid-cols-3 items-center gap-4">
        <Label htmlFor="retention" className="col-span-1">
          Default retention (days)
        </Label>
        <Input
          id="retention"
          type="number"
          min={1}
          max={3650}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="col-span-2 max-w-[200px]"
          data-testid="settings-retention"
        />
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={saving} data-testid="settings-recordings-save">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save retention
        </Button>
      </div>
    </form>
  );
}

function StorageSection({ data, saving, onSave }) {
  const [mode, setMode] = useState(data.storage_mode);
  const [endpoint, setEndpoint] = useState(data.storage_s3_endpoint);
  const [bucket, setBucket] = useState(data.storage_s3_bucket);
  const [region, setRegion] = useState(data.storage_s3_region);
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const payload = { storage_mode: mode };
    if (endpoint !== data.storage_s3_endpoint) payload.storage_s3_endpoint = endpoint;
    if (bucket !== data.storage_s3_bucket) payload.storage_s3_bucket = bucket;
    if (region !== data.storage_s3_region) payload.storage_s3_region = region;
    if (accessKey) payload.storage_s3_access_key = accessKey;
    if (secretKey) payload.storage_s3_secret_key = secretKey;
    onSave(payload);
    setAccessKey("");
    setSecretKey("");
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-5 rounded-lg border border-border bg-card p-6"
      data-testid="settings-section-storage"
    >
      <div className="grid grid-cols-3 items-center gap-4">
        <Label className="col-span-1">Mode</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="col-span-2 max-w-[200px]" data-testid="settings-storage-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="local">Local disk</SelectItem>
            <SelectItem value="s3">S3-compatible</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === "s3" && (
        <>
          <Field label="Endpoint" value={endpoint} onChange={setEndpoint} testid="settings-s3-endpoint" />
          <Field label="Bucket" value={bucket} onChange={setBucket} testid="settings-s3-bucket" />
          <Field label="Region" value={region} onChange={setRegion} testid="settings-s3-region" />
          <SecretField
            label="Access key"
            placeholder={data.has_storage_s3_access_key ? "*** (set)" : "(not set)"}
            value={accessKey}
            onChange={setAccessKey}
            testid="settings-s3-access-key"
          />
          <SecretField
            label="Secret key"
            placeholder={data.has_storage_s3_secret_key ? "*** (set)" : "(not set)"}
            value={secretKey}
            onChange={setSecretKey}
            testid="settings-s3-secret-key"
          />
        </>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} data-testid="settings-storage-save">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save storage
        </Button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-3 items-center gap-4">
      <Label className="col-span-1">{label}</Label>
      <Input
        className="col-span-2"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
function SecretField({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="grid grid-cols-3 items-center gap-4">
      <Label className="col-span-1">{label}</Label>
      <Input
        className="col-span-2"
        type="password"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}


function WorkerSection({ data, saving, onSave }) {
  const [values, setValues] = useState(() => ({
    worker_concurrency_cap: data.worker_concurrency_cap ?? 100,
    recording_enabled: data.recording_enabled ?? true,
    default_recording_retention_days: data.default_recording_retention_days ?? 30,
  }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      worker_concurrency_cap: Number(values.worker_concurrency_cap),
      recording_enabled: !!values.recording_enabled,
      default_recording_retention_days: Number(values.default_recording_retention_days),
    });
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-5 rounded-lg border border-border bg-card p-6"
      data-testid="settings-section-worker"
    >
      <Field
        label="Global concurrency cap"
        value={values.worker_concurrency_cap}
        onChange={(v) => setValues((s) => ({ ...s, worker_concurrency_cap: v }))}
        testid="settings-worker-concurrency-cap"
      />
      <p className="text-xs text-muted-foreground -mt-3 ml-[33%]">
        Maximum simultaneous calls across the whole fleet (1–1000). Each worker
        also enforces its own local soft cap.
      </p>

      <div className="grid grid-cols-3 items-center gap-4">
        <Label className="col-span-1">Recording enabled</Label>
        <div className="col-span-2 flex items-center gap-3">
          <input
            type="checkbox"
            checked={!!values.recording_enabled}
            onChange={(e) => setValues((s) => ({ ...s, recording_enabled: e.target.checked }))}
            data-testid="settings-worker-recording-enabled"
            className="h-4 w-4 accent-primary"
          />
          <span className="text-xs text-muted-foreground">
            When off, the worker skips LiveKit Egress and no recordings are uploaded.
          </span>
        </div>
      </div>

      <Field
        label="Retention (days)"
        value={values.default_recording_retention_days}
        onChange={(v) => setValues((s) => ({ ...s, default_recording_retention_days: v }))}
        testid="settings-worker-retention-days"
      />
      <p className="text-xs text-muted-foreground -mt-3 ml-[33%]">
        After this many days the retention sweeper deletes the recording.
        Set <b>0</b> to disable deletion entirely (recordings kept forever).
      </p>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} data-testid="settings-worker-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save Worker settings
        </Button>
      </div>
    </form>
  );
}
